import { Link } from "react-router";

import { GuideArticle, Section } from "../GuideShell";

export function ScoreBandsPage() {
  return (
    <GuideArticle
      slug="score-bands"
      title="Score bands & thresholds"
      intro={
        <p>
          Rarion summarises an assessment with a neutral score band — never a "hireable / not
          hireable" verdict. The system surfaces whether the evidence meets the bar; the recruiter
          decides what to do about it.
        </p>
      }
    >
      <Section id="bands" title="The score bands">
        <p>
          Each completed assessment lands in one of three bands, derived from the per-dimension
          scores against the blueprint's required levels:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Missing requirements:</strong> material gaps against the bar on one or more
            critical dimensions.
          </li>
          <li>
            <strong>Meets requirements:</strong> at the bar across the dimensions that matter.
          </li>
          <li>
            <strong>Exceeds requirements:</strong> above the bar, with strong evidence on the
            highest-weighted dimensions.
          </li>
        </ul>
        <p>
          The band is a summary signal — the actionable detail is in the per-dimension scores and
          their anchors.
        </p>
      </Section>

      <Section id="thresholds" title="Threshold rules">
        <p>Each blueprint can carry a threshold rule with three knobs:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Critical-dimension minimums:</strong> the lowest acceptable score on each
            dimension marked critical (e.g. 3+ on every critical competency).
          </li>
          <li>
            <strong>Overall minimum:</strong> the minimum aggregate score across all dimensions.
          </li>
          <li>
            <strong>Strong-meets count (optional):</strong> the minimum number of dimensions on
            which the candidate must score 4 or higher.
          </li>
        </ul>
        <p>
          When all knobs are met, the candidate is flagged <strong>Above threshold</strong>. When
          any is not met, the flag is <strong>Below threshold</strong>.
        </p>
      </Section>

      <Section id="override" title="Recruiter override">
        <p>
          The threshold is a signal, not a gate. A recruiter can override "Below threshold" with a
          logged reason — that override is recorded on the assessment, surfaced on the candidate's
          pipeline card, and visible in the shortlist report. The intent is to keep the cohort going
          into a panel discussion honest about who is above or below the bar, while leaving the
          actual hire / no-hire judgement with the people accountable for it.
        </p>
      </Section>

      <Section id="why-no-verdict" title="Why the system never decides hireability">
        <p>
          The system extracts evidence and computes whether scores clear the bar; it does not decide
          whether to hire. Hireability depends on context the system does not see — the rest of the
          cohort, the client's other priorities, the team shape — and conflating "meets
          requirements" with "should be hired" weakens the trail when the decision is questioned
          later.
        </p>
        <p>
          So the bands are deliberately neutral. "Meets requirements" is a statement about evidence;
          the move from <em>meets</em> to <em>hire</em> is a stage transition the recruiter
          performs, not a label the system applies.
        </p>
        <p>
          See{" "}
          <Link to="/help/scoring-scales#thresholds" className="text-link hover:underline">
            scoring scales: threshold rules
          </Link>{" "}
          for how thresholds and the dual-scale model fit together.
        </p>
      </Section>
    </GuideArticle>
  );
}
